/**
 * Step 4's rotation as the editor holds it (SA-449, spec §7.2): the stops
 * as a chain and one sector between each pair — live (the customer's cargo
 * aboard) or positioning (the aircraft moving). Pure: every edit returns a
 * new Rotation, and `sectors.length` is always `max(0, stops.length - 1)`.
 *
 * `same` is the payload box: "Same as required payload" on a live sector,
 * "Flies empty" on a positioning one. It is a form convenience — what is
 * stored is explicit tonnes (`toRows`), and the ticks are derived back from
 * the rows on reload (`fromRows`).
 *
 * SA-1052: an end the customer gave alternatives for is a stop with `offers` —
 * unchosen (code '') until a person picks one; the rows hold codes only, so the
 * stop IS the record of the choice.
 */
import { placeAlternatives, placeCode } from '@/components/records/place'

export type FlownAs = 'live' | 'positioning'
export interface SectorState { flownAs: FlownAs; same: boolean; tonnes: number | null }
export interface Rotation {
  stops: string[]
  sectors: SectorState[]
  /** SA-1052: per stop, the codes it may be; absent when no stop has any. */
  offers?: string[][]
}
export interface SectorRow { from: string; to: string; flown_as: FlownAs; payload: number | null }

const ticked = (flownAs: FlownAs): SectorState => ({ flownAs, same: true, tonnes: null })
const stop = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

/** SA-1051 (spec §9): a stop is seeded only from a place that names exactly one
 * code — a legacy `Liège Airport (LGG)` seeds `LGG`; a choice or free text seeds
 * nothing, so the two-stops rule asks the person. */
export const seedStop = (value: unknown): string => placeCode(value) ?? ''

export const offersAt = (r: Rotation, i: number): string[] => r.offers?.[i] ?? []
/** A stop in words: its code, or its offers while it is not chosen ("AUH or DHF"). */
export const stopName = (r: Rotation, i: number): string => r.stops[i] || offersAt(r, i).join(' or ')
export const unchosen = (r: Rotation, i: number): boolean => !r.stops[i] && offersAt(r, i).length > 0

/** Keep `offers` parallel to `stops`, and absent when no stop has any. */
function withOffers(stops: string[], sectors: SectorState[], offers: string[][] | undefined): Rotation {
  return offers && offers.some((o) => o.length > 0) ? { stops, sectors, offers } : { stops, sectors }
}

const offersFor = (code: string, ends: unknown[]): string[] =>
  ends.map(placeAlternatives).find((alternatives) => alternatives.includes(code)) ?? []

/** SA-1052: the person picks which of its offers a stop is; anything else is no change. */
export function chooseStop(r: Rotation, i: number, code: string): Rotation {
  if (!offersAt(r, i).includes(code)) return r
  return { ...r, stops: r.stops.map((s, k) => (k === i ? code : s)) }
}

/** SA-1331: put a station outside the alternatives in a stop's place. An alternative is `chooseStop`
 * (offers kept, as a reload derives); any other station leaves the stop with no offers, as a reload
 * would. The sectors are untouched. */
export function replaceStop(r: Rotation, i: number, code: string): Rotation {
  if (offersAt(r, i).includes(code)) return chooseStop(r, i, code)
  const stops = r.stops.map((s, k) => (k === i ? code : s))
  return withOffers(stops, r.sectors, r.offers?.map((o, k) => (k === i ? [] : o)))
}

/** The stored rows that read as sectors (SA-449 §7.2; the record's Route card reads through it too, SA-451). */
export function readRows(value: unknown): SectorRow[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((row) => {
    if (!row || typeof row !== 'object') return []
    const r = row as Record<string, unknown>
    const from = stop(r.from), to = stop(r.to)
    if (!from || !to) return []
    const payload = typeof r.payload === 'number' && Number.isFinite(r.payload) ? r.payload : null
    return [{ from, to, flown_as: r.flown_as === 'live' ? 'live' : 'positioning', payload } as SectorRow]
  })
}

/** The stored rows, read as a chain: the stops are the first row's `from`
 * and every row's `to` (a gap left by another writer closes up on save). */
export function fromRows(rows: SectorRow[], required: number | null): Rotation {
  if (rows.length === 0) return { stops: [], sectors: [] }
  return {
    stops: [rows[0].from, ...rows.map((r) => r.to)],
    sectors: rows.map((r) => {
      const same = r.flown_as === 'live' ? r.payload === null || r.payload === required : r.payload === null
      return { flownAs: r.flown_as, same, tonnes: same ? null : r.payload }
    }),
  }
}

/** The stored value if it holds sectors (a stop that is one of an end's
 * alternatives gets them back as its offers), else one live sector from the
 * intake's origin to its destination — an end with alternatives seeded as an
 * unchosen stop (SA-1052). */
export function seedRotation(stored: unknown, origin: unknown, destination: unknown, required: number | null): Rotation {
  const ends = [origin, destination]
  const rows = readRows(stored)
  if (rows.length) {
    const r = fromRows(rows, required)
    return withOffers(r.stops, r.sectors, r.stops.map((s) => offersFor(s, ends)))
  }
  const seeded = ends.map((v) => ({ code: seedStop(v), offers: placeAlternatives(v) })).filter((e) => e.code || e.offers.length > 0)
  const stops = seeded.map((e) => e.code)
  return withOffers(stops, stops.length === 2 ? [ticked('live')] : [], seeded.map((e) => e.offers))
}

export function toRows(r: Rotation, required: number | null): SectorRow[] {
  return r.sectors.map((s, i) => {
    const live = s.flownAs === 'live'
    const payload = s.same ? (live ? required : null) : !live && s.tonnes === 0 ? null : s.tonnes
    return { from: r.stops[i], to: r.stops[i + 1], flown_as: s.flownAs, payload }
  })
}

/** A stop added at either end of a chain that has one adds a flight, so the
 * editor asks what it is for; a stop between two splits a flight that
 * already has an answer; the very first stop is no flight at all. */
export function asksFlownAs(r: Rotation, at: number): boolean {
  return r.stops.length > 0 && (at === 0 || at === r.stops.length)
}

export function addStop(r: Rotation, at: number, code: string, flownAs: FlownAs = 'positioning'): Rotation {
  const n = r.stops.length
  const stops = [...r.stops.slice(0, at), code, ...r.stops.slice(at)]
  const offers = r.offers ? [...r.offers.slice(0, at), [], ...r.offers.slice(at)] : undefined
  let sectors: SectorState[]
  if (n === 0) sectors = []
  else if (at === 0) sectors = [ticked(flownAs), ...r.sectors]
  else if (at === n) sectors = [...r.sectors, ticked(flownAs)]
  else {
    const split = r.sectors[at - 1]
    sectors = [...r.sectors.slice(0, at - 1), { ...split }, { ...split }, ...r.sectors.slice(at)]
  }
  return withOffers(stops, sectors, offers)
}

/** Taking out a stop between two flights leaves one flight doing both jobs:
 * the first half when it carried the customer's cargo, else the second. */
export function removeStop(r: Rotation, i: number): Rotation {
  const n = r.stops.length
  const stops = r.stops.filter((_, k) => k !== i)
  const offers = r.offers?.filter((_, k) => k !== i)
  let sectors: SectorState[]
  if (n <= 1) sectors = []
  else if (i === 0) sectors = r.sectors.slice(1)
  else if (i === n - 1) sectors = r.sectors.slice(0, -1)
  else {
    const [first, second] = [r.sectors[i - 1], r.sectors[i]]
    const kept = first.flownAs === 'live' ? first : second
    sectors = [...r.sectors.slice(0, i - 1), { ...kept }, ...r.sectors.slice(i + 1)]
  }
  return withOffers(stops, sectors, offers)
}

const replaceAt = (r: Rotation, i: number, next: SectorState): Rotation =>
  ({ ...r, sectors: r.sectors.map((s, k) => (k === i ? next : s)) })

export function setFlownAs(r: Rotation, i: number, flownAs: FlownAs): Rotation {
  return replaceAt(r, i, ticked(flownAs))
}

/** Unticked, a live sector's box opens on the required payload, so a stretch
 * that carries less is typed down from the full load, not up from nothing. */
export function setSame(r: Rotation, i: number, same: boolean, required: number | null): Rotation {
  const s = r.sectors[i]
  const tonnes = same ? null : s.tonnes ?? (s.flownAs === 'live' ? required : null)
  return replaceAt(r, i, { ...s, same, tonnes })
}

export function setTonnes(r: Rotation, i: number, tonnes: number | null): Rotation {
  return replaceAt(r, i, { ...r.sectors[i], tonnes })
}

const sectorName = (r: Rotation, i: number) => `${stopName(r, i)} to ${stopName(r, i + 1)}`

/** The first reason the form cannot be completed (spec §6.2; SA-1052 §6.1), or null. */
export function rotationProblem(r: Rotation): string | null {
  if (r.stops.length < 2) return 'The route needs at least two stops.'
  const open = r.stops.findIndex((_, i) => unchosen(r, i))
  if (open >= 0) return `Choose which of ${offersAt(r, open).join(' or ')} the route flies to.`
  // A sector that starts and ends at the same stop is never valid (a reload can restore two adjacent offers' choices as one code).
  const same = r.stops.findIndex((s, i) => i > 0 && s !== '' && s === r.stops[i - 1])
  if (same > 0) return `${sectorName(r, same - 1)} starts and ends at the same stop: choose another.`
  if (!r.sectors.some((s) => s.flownAs === 'live')) return 'At least one sector has to be live: the flight the customer asked for.'
  for (let i = 0; i < r.sectors.length; i++) {
    const s = r.sectors[i]
    if (!s.same && s.tonnes === null) return `${sectorName(r, i)}: enter its payload, or tick the box back.`
    if (!s.same && s.flownAs === 'live' && s.tonnes !== null && s.tonnes <= 0) {
      return `${sectorName(r, i)} is live, so it carries cargo: enter more than zero, or mark it positioning.`
    }
  }
  return null
}

/** The live sectors joined up, run by run: a positioning sector between two
 * live ones ends a run, a technical stop inside one does not (SA-451 spec §6;
 * the Python twin is `sectors.live_runs`, pinned by shared/sector-cases.json). */
export function liveRuns(r: Rotation): string[][] {
  const runs: string[][] = []
  let current: string[] | null = null
  r.sectors.forEach((s, i) => {
    if (s.flownAs !== 'live') { current = null; return }
    if (!current) { current = [r.stops[i]]; runs.push(current) }
    current.push(r.stops[i + 1])
  })
  return runs
}

const named = (r: Rotation): Rotation => ({ ...r, stops: r.stops.map((_, i) => stopName(r, i)) })

/** The live runs in words; two runs with a positioning sector between them
 * read as two routes. Arrow-joined to match the register (SA-453 D6) —
 * the backend twin's `sectors.ROUTE_SEP`. */
export function cargoRoute(r: Rotation): string {
  return liveRuns(named(r)).map((run) => run.join(' → ')).join(', ')
}

/** A stop is part of the cargo route when the sector before it or after it
 * is live — the chain draws every other stop as positioning. */
export function stopLive(sectors: SectorState[], i: number): boolean {
  return sectors[i - 1]?.flownAs === 'live' || sectors[i]?.flownAs === 'live'
}

/** Everything the aircraft flies. Arrow-joined to match the register
 * (SA-453 D6) — the backend twin's `sectors.ROUTE_SEP`. */
export function proposedRoute(r: Rotation): string {
  return named(r).stops.join(' → ')
}
